const BODY_LOCK_CLASS = 'lock';

export const lockBody = () =>
  document.documentElement.classList.add(BODY_LOCK_CLASS);

export const unlockBody = () =>
  document.documentElement.classList.remove(BODY_LOCK_CLASS);

export const focusElement = element => {
  element.focus();
  return element;
};

/**
 * Returns the first item of an array.
 *
 * head :: [a] -> a
 *
 * @param {Array} x the array to get the first item from.
 */
export const head = ([x]) => x;

/**
 * Returns all but the first item of an array.
 *
 * tail :: [a] -> [a]
 *
 * @param {Array} x the array to get all but the first item from.
 */
export const tail = ([, ...xs]) => xs;

/**
 * Returns the last element of an array or list
 * @param {Array} arrayOrString
 */
export const last = arrayOrString => {
  if (typeof arrayOrString === 'string')
    return arrayOrString[arrayOrString.length - 1] || '';

  return arrayOrString[arrayOrString.length - 1];
};

/**
 * Returns a reversed array. Uses the inbuilt Array.reverse for arrays and
 * split > reverse > join for strings.
 *
 * @param {Array} arrayOrString
 */
export const reverse = arrayOrString => {
  if (typeof arrayOrString === 'string')
    return arrayOrString
      .split('')
      .reverse()
      .join('');

  const clone = arrayOrString.concat();
  return clone.reverse();
};

/**
 * Composes functions together
 * @param  {...any} fns functions to compose
 * @returns {function}
 */
export const compose = (...fns) =>
  fns.reduce((f, g) => (...args) => f(g(...args)));

/**
 * Pipes functions together
 * @param  {...any} fns functions to pipe
 * @returns {function}
 */
export const pipe = (...fns) => x => fns.reduce((v, f) => f(v), x);

export const trace = label => value => {
  console.log(`${label}: ${value}`);
  return value;
};

export const addClass = className => element => {
  element.classList.add(className);
};

export const removeClass = className => element => {
  element.classList.remove(className);
};

export const toggleClass = (className, force = true) => element => {
  element.classList.toggle(className, force);
};

export const matcher = selector => event => event.target.matches(selector);

export const closer = selector => event => event.target.closest(selector);

export const isNull = x => x == null;
export const isNotNull = x => !isNull(x);

export const getClosest = (elem, selector) => {

  // Element.matches() polyfill
  if (!Element.prototype.matches) {
      Element.prototype.matches =
          Element.prototype.matchesSelector ||
          Element.prototype.mozMatchesSelector ||
          Element.prototype.msMatchesSelector ||
          Element.prototype.oMatchesSelector ||
          Element.prototype.webkitMatchesSelector ||
          function (s) {
              var matches = (this.document || this.ownerDocument).querySelectorAll(s),
                  i = matches.length;
              while (--i >= 0 && matches.item(i) !== this) { }
              return i > -1;
          };
  }

  // Get the closest matching element
  for (; elem && elem !== document; elem = elem.parentNode) {
      if (elem.matches(selector)) return elem;
  }
  return null;
};

/**
 * Get the next or previous element from a given start point
 * @param  {HTMLElement} startEl    Element to start position from
 * @param  {String}      className  The class we will look through
 * @param  {Number}      direction  Positive next element, negative previous element
 * @return {[HTMLElement}           Found element
 */
export const getAdjacentEl = (startEl, className, direction = 1) => {
  if (!startEl || !className) return;

  const parent = startEl.parentNode;
  const children = Array.from(parent.querySelectorAll(className));

  const startPos = children.indexOf(startEl);
  const operatorDirection = direction > 0 ? 1 : -1;

  return children[startPos + operatorDirection];
};

/**
 * Get a random number between a range
 * @param  {Number} min Minimum range
 * @param  {Number} max Maximum range
 * @return {Number}     Random number
 */
export const getRandomNumber = function (min, max) {
  return Math.floor(Math.random() * (max - min) + min);
};

/**
* Generates a string of random chars
* @param  {Number} length Length of the string to generate
* @return {String} String of random chars
*/
export const generateChars = function (length) {
  let chars = '';

  for (let i = 0; i < length; i++) {
      const randomChar = getRandomNumber(0, 36);
      chars += randomChar.toString(36);
  }

  return chars;
};

/**
 * Generates a unique id based on an element
 * @param  {HTMLElement} element Element to generate the id from
 * @param  {String} prefix for the Id
 * @return {String} Unique Id
 */
export const generateId = function (element, prefix) {
  let id = element.id || (element.name && (`${element.name}-${generateChars(2)}`)) || generateChars(4);
  id = id.replace(/(:|\.|\[|\]|,)/g, '');
  id = prefix + id;

  return id;
};

/**
 * Creates a debounced function that wont fire until [wait] milliseconds after it was last called
 * @param {function(...[*])} func The function to call
 * @param {Number} wait time in milliseconds
 * @returns {function(...[*]=)}
 */
export const debounce = (func, wait) => {
  let timeout;

  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }
};

/**
 * Creates a DocumentFragment with the inner html provided
 * @param {string} Html string
 * @returns {DocumentFragment} Fragment containing the provided html
 */
export const createFragment = (innerHtml) => {
  const fragment = document.createDocumentFragment();
  const wrapper = document.createElement('div');

  wrapper.innerHTML = innerHtml;
  while(wrapper.firstChild){
    fragment.appendChild(wrapper.firstChild);
  }
  return fragment;
};

/**
 * Attach an event to parent and delegate the trigger to children matching the provided selector
 * @param {Element} parent
 * @returns {function(string,string,function(Element, Event))}
 */
export const delegatedEvent = parent =>
  /**
   *
   * @param {string} eventName
   * @param {string} elementSelector
   * @param {function(EventTarget, Event)} handler
   */
  // please do what i want mr jsdoc :(
    (eventName, elementSelector, handler) => {
    parent.addEventListener(eventName, event => {
      for (
        let target = event.target;
        target && target !== parent;
        target = target.parentNode
      ) {
        if (target.matches(elementSelector)) {
          handler(target, event);
          break;
        }
      }
    });
  };

